import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Search, Check } from 'lucide-react';
import api from '../../lib/api';

const CATEGORY_LABELS = {
  mecanique: 'Mécanique',
  univers: 'Univers',
  licence: 'Licence',
  recompense: 'Récompense',
  theme: 'Thème',
  gamme: 'Gamme',
  autre: 'Autre',
};

// Phase 2.3.5: Unified B2B chip style — no category colors
const CHIP_CLASS = 'bg-gray-100 text-gray-700 border-gray-200';

export default function TagsProductSelect({ selectedIds = [], onChange, disabled = false }) {
  const [allTags, setAllTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Find the best portal target: Radix dialog portal container (inside FocusScope)
  // or fallback to document.body
  const getPortalTarget = useCallback(() => {
    if (!triggerRef.current) return document.body;
    // Walk up to find the Radix portal container
    const radixPortal = triggerRef.current.closest('[data-radix-portal]');
    return radixPortal || document.body;
  }, []);

  useEffect(() => {
    api.get('/referentiels/tags_produit?include_inactive=false')
      .then(res => setAllTags(res.data?.items || []))
      .catch(() => {});
  }, []);

  // Position dropdown relative to trigger via portal
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Open/close
  const handleOpen = useCallback(() => {
    if (disabled) return;
    if (!open) {
      updatePosition();
      setOpen(true);
      setSearch('');
    } else {
      setOpen(false);
    }
  }, [open, disabled, updatePosition]);

  // Close on outside click — must check both trigger and portal dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Use timeout to avoid immediate close on the opening click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); // Prevent dialog from closing
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler, true); // capture phase
    return () => document.removeEventListener('keydown', handler, true);
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, updatePosition]);

  // Focus search input after portal mounts (bypass Radix FocusScope race)
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const selectedTags = useMemo(() =>
    allTags.filter(t => selectedIds.includes(t.id)),
    [allTags, selectedIds]
  );

  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = allTags.filter(t =>
      t.is_active && t.value.toLowerCase().includes(q)
    );
    const groups = {};
    // Maintain category order
    const catOrder = ['mecanique', 'univers', 'licence', 'recompense', 'theme', 'gamme', 'autre'];
    filtered.forEach(t => {
      const cat = t.category || 'autre';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    // Sort by defined order
    const sorted = {};
    catOrder.forEach(c => { if (groups[c]) sorted[c] = groups[c]; });
    // Append any remaining
    Object.keys(groups).forEach(c => { if (!sorted[c]) sorted[c] = groups[c]; });
    return sorted;
  }, [allTags, search]);

  const toggle = useCallback((tagId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    const newIds = selectedIds.includes(tagId)
      ? selectedIds.filter(id => id !== tagId)
      : [...selectedIds, tagId];
    onChange(newIds);
    // Dropdown stays open — multi-select behavior
  }, [selectedIds, onChange, disabled]);

  const remove = useCallback((tagId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    onChange(selectedIds.filter(id => id !== tagId));
  }, [selectedIds, onChange, disabled]);

  // Tooltip: show category on hover
  const getTooltip = (tag) => {
    const catLabel = CATEGORY_LABELS[tag.category] || tag.category || '';
    return catLabel ? `${tag.value}\n${catLabel}` : tag.value;
  };

  const totalResults = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <>
      {/* Trigger area */}
      <div ref={triggerRef} data-testid="tags-product-select">
        <div
          className={`min-h-[38px] flex flex-wrap gap-1 p-1.5 border rounded-md cursor-pointer transition-colors ${
            open ? 'border-primary ring-1 ring-primary/20' : 'border-gray-200 hover:border-gray-300'
          } ${disabled ? 'opacity-60 pointer-events-none bg-gray-50' : 'bg-white'}`}
          onClick={handleOpen}
          data-testid="tags-trigger"
        >
          {selectedTags.length === 0 && (
            <span className="text-sm text-muted-foreground px-1 py-0.5">Sélectionner des tags...</span>
          )}
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${CHIP_CLASS}`}
              title={getTooltip(tag)}
              data-testid={`tag-chip-${tag.id}`}
            >
              {tag.value}
              <button
                type="button"
                onClick={(e) => remove(tag.id, e)}
                className="ml-0.5 hover:opacity-70 focus:outline-none"
                tabIndex={-1}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <ChevronDown className={`h-4 w-4 ml-auto self-center text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown via Portal — inside Radix portal (FocusScope) if in dialog, else document.body */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border rounded-lg shadow-xl"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
            maxHeight: '320px',
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onFocusCapture={(e) => e.stopPropagation()}
          data-testid="tags-dropdown"
        >
          {/* Search */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un tag..."
                className="w-full pl-7 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/30"
                onFocus={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setOpen(false);
                  }
                }}
                data-testid="tags-search-input"
              />
            </div>
          </div>

          {/* Scrollable list */}
          <div
            className="overflow-y-auto p-1"
            style={{ maxHeight: '260px' }}
            onWheel={(e) => e.stopPropagation()}
          >
            {totalResults === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">Aucun tag trouvé</p>
            )}
            {Object.entries(grouped).map(([cat, tags]) => (
              <div key={cat} className="mb-1">
                <p className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 bg-white">
                  {CATEGORY_LABELS[cat] || cat}
                </p>
                {tags.map(tag => {
                  const isSelected = selectedIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={(e) => toggle(tag.id, e)}
                      className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${
                        isSelected ? 'bg-gray-100 font-medium text-gray-900' : 'hover:bg-gray-50 text-gray-700'
                      }`}
                      data-testid={`tag-option-${tag.id}`}
                    >
                      <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                        isSelected ? 'bg-gray-800 border-gray-800' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="flex-1">{tag.value}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer count */}
          <div className="px-3 py-1.5 border-t bg-gray-50 text-[10px] text-muted-foreground">
            {selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''} · {totalResults} résultat{totalResults > 1 ? 's' : ''}
          </div>
        </div>,
        getPortalTarget()
      )}
    </>
  );
}
